/**
 * Tab "Giải Pickleague" trong NotificationsPage.
 * Hiển thị danh sách giải đấu Pickleague (sync từ API), stats, danh sách cặp VĐV,
 * cấu hình ZNS template ID + STK default, gửi ZNS thủ công cho từng cặp/hàng loạt.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, Settings as SettingsIcon, Send, Trophy, CheckCircle2, XCircle, Clock, Users, FileText, Download, Webhook, Tag, AlertCircle, Eye, SendHorizonal, RotateCw, Trash2, Wallet, WalletCards } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function PickleagueTab() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState("");
  const [pairs, setPairs] = useState({ pairs: [], total: 0 });
  const [pairsLoading, setPairsLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [configEditing, setConfigEditing] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [sendingRow, setSendingRow] = useState(null); // v1.3.1: row key = `${pair_id}__${player_id}`
  const [sendingBulk, setSendingBulk] = useState(false);
  const [expandedPair, setExpandedPair] = useState(null);
  // v1.1
  const [apiVersion, setApiVersion] = useState("");
  const [webhookLogs, setWebhookLogs] = useState(null);
  const [webhookLogsLoading, setWebhookLogsLoading] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  // v1.2 — Webhook protection state
  const [webhookState, setWebhookState] = useState(null);
  const [resettingCircuit, setResettingCircuit] = useState(false);
  // v1.3.1 — Preview ZNS dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  // v1.3.2 — Bulk selection
  const [selectedRows, setSelectedRows] = useState(new Set()); // row_key set
  const [bulkSending, setBulkSending] = useState(false);
  // v1.5.4: bulk progress + log realtime
  const [bulkProgress, setBulkProgress] = useState(null);
  // v1.5.5: filter theo nội dung thi đấu (category)
  const [categoryFilter, setCategoryFilter] = useState("all");
  // shape: {total, done, ok, failed, skipped, items: [{row_key, name, phone, status, error}]}

  const loadVersion = useCallback(async () => {
    try {
      const { data } = await api.get("/pickleague/version");
      setApiVersion(data.version || "");
    } catch (e) { /* ignore */ }
  }, []);

  const loadTournaments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pickleague/tournaments");
      setTournaments(data.tournaments || []);
    } catch (e) {
      toast.error("Không tải được danh sách giải Pickleague");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPairs = useCallback(async (code, statusFilter = filter) => {
    if (!code) return;
    setPairsLoading(true);
    try {
      // v1.4.0: nếu filter "removed" → load pairs đã rút (sync=removed), còn lại default sync=active
      const syncParam = statusFilter === "removed" ? "removed" : "active";
      const statusParam = statusFilter === "removed" ? "all" : statusFilter;
      const { data } = await api.get(`/pickleague/tournaments/${encodeURIComponent(code)}/pairs?status=${statusParam}&sync=${syncParam}&limit=500`);
      setPairs(data);
    } catch (e) {
      toast.error("Không tải được danh sách cặp");
    } finally {
      setPairsLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadTournaments(); loadVersion(); }, [loadTournaments, loadVersion]);
  useEffect(() => { if (selectedCode) loadPairs(selectedCode, filter); }, [selectedCode, filter, loadPairs]);

  const loadWebhookLogs = async (code) => {
    if (!code) return;
    setWebhookLogsLoading(true);
    try {
      const [logsRes, stateRes] = await Promise.all([
        api.get(`/pickleague/tournaments/${encodeURIComponent(code)}/webhook-logs?limit=30`),
        api.get(`/pickleague/tournaments/${encodeURIComponent(code)}/webhook-state`).catch(() => null),
      ]);
      setWebhookLogs(logsRes.data);
      setWebhookState(stateRes?.data || null);
    } catch { /* ignore */ } finally {
      setWebhookLogsLoading(false);
    }
  };

  const handleResetCircuit = async (code) => {
    if (!window.confirm("Reset circuit breaker về CLOSED ngay? Webhook sẽ gửi lại được liền (không chờ 30p)")) return;
    setResettingCircuit(true);
    try {
      await api.post(`/pickleague/tournaments/${encodeURIComponent(code)}/webhook-reset-circuit`);
      toast.success("Đã reset circuit breaker. Webhook có thể gửi lại ngay.");
      await loadWebhookLogs(code);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi reset circuit");
    } finally {
      setResettingCircuit(false);
    }
  };

  const handleTestWebhook = async (code) => {
    setTestingWebhook(true);
    try {
      const { data } = await api.post(`/pickleague/tournaments/${encodeURIComponent(code)}/webhook-test`);
      toast.success(data.message || "Đã gửi webhook test");
      setTimeout(() => loadWebhookLogs(code), 2000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi test webhook");
    } finally {
      setTestingWebhook(false);
    }
  };

  const selected = tournaments.find(t => t.tournament_code === selectedCode);

  // v1.3.1: Flatten pairs.pairs[].players[] thành 1 row per VĐV (giống pattern CRM giải đấu)
  const playerRows = useMemo(() => {
    const rows = [];
    (pairs.pairs || []).forEach((p, pairIdx) => {
      const players = p.players || [];
      const isRemoved = p.sync_status === "removed";
      // v1.4.3: detect "Đôi" missing partner — category chứa "Đôi" nhưng chỉ 1 player
      const categoryLower = (p.category || "").toLowerCase();
      const isDoubles = categoryLower.includes("đôi") || categoryLower.includes("doi") || categoryLower.includes("double");
      const missingPartner = isDoubles && players.length < 2;
      players.forEach((player, playerIdx) => {
        const partner = players.find(x => x.player_id !== player.player_id);
        const perPlayerZns = (p.zns_per_player || {})[player.player_id] || {};
        rows.push({
          row_key: `${p.pair_id}__${player.player_id || playerIdx}`,
          display_stt: `${pairIdx + 1}.${playerIdx + 1}`,
          pair_id: p.pair_id,
          player_id: player.player_id || "",
          full_name: player.full_name || "",
          nickname: player.nickname || "",
          phone: player.phone || "",
          phone_valid: !!(player.phone && /^(0|\+84|84)(3|5|7|8|9)\d{8}$/.test(player.phone)),
          level: player.level || "",
          province: player.province || "",
          category: p.category || "",
          partner_name: partner?.full_name || "",
          partner_phone: partner?.phone || "",
          transfer_content: p.transfer_content || "",
          // v1.4.7: token đầu của transfer_content; fallback pair_id (split _ nếu dài); truncate 24 chars
          contract_number: (() => {
            const MAX = 24;
            const full = (p.transfer_content || "").trim();
            let token = full ? full.split(/\s+/)[0] : "";
            if (!token) {
              const pid = String(p.pair_id || "");
              token = (pid.length > MAX && pid.includes("_")) ? pid.split("_").pop() : pid;
            }
            return token.slice(0, MAX);
          })(),
          // v1.4.9: bank_transfer_note = full transfer_content (truncate 90)
          bank_transfer_note: (() => {
            const MAX = 90;
            const full = ((p.transfer_content || "").trim()) || String(p.pair_id || "");
            return full.slice(0, MAX);
          })(),
          fee: p.fee || 0,
          // v1.5.2 — breakdown từ Pickleague combined payment (lệ phí + tài trợ)
          breakdown: p.breakdown || null,
          payment_status: p.payment_status || "unpaid",
          zns_status: perPlayerZns.status || p.zns_status || "not_sent",
          zns_error: perPlayerZns.error || p.zns_last_error || "",
          // v1.4.0
          sync_status: p.sync_status || "active",
          is_removed: isRemoved,
          removed_reason: p.removed_reason || "",
          removed_at: p.removed_at || null,
          // v1.4.3
          missing_partner: missingPartner,
          missing_transfer_content: !p.transfer_content,
        });
      });
    });
    return rows;
  }, [pairs.pairs]);

  // v1.5.5: Filter category client-side
  const displayRows = useMemo(() => {
    if (categoryFilter === "all") return playerRows;
    return playerRows.filter(r => (r.category || "(chưa phân loại)") === categoryFilter);
  }, [playerRows, categoryFilter]);

  // v1.3.2: Các row keys eligible cho bulk send (phone hợp lệ + chưa TT + chưa removed)
  const eligibleRowKeys = useMemo(
    () => playerRows.filter(r => r.phone_valid && r.payment_status !== "paid" && !r.is_removed).map(r => r.row_key),
    [playerRows],
  );

  // v1.5.5: Bộ lọc CRM-style — stats computed
  // Stats từ playerRows (theo giải đang chọn)
  const rowStats = useMemo(() => {
    const s = { total: 0, paid: 0, unpaid: 0, zns_sent: 0, zns_not_sent: 0, zns_failed: 0, removed: 0 };
    playerRows.forEach(r => {
      s.total += 1;
      if (r.is_removed) { s.removed += 1; return; }
      if (r.payment_status === "paid") s.paid += 1; else s.unpaid += 1;
      if (r.zns_status === "sent") s.zns_sent += 1;
      else if (r.zns_status === "failed") s.zns_failed += 1;
      else s.zns_not_sent += 1;
    });
    return s;
  }, [playerRows]);

  // Categories có trong giải (vd "Đôi hỗn hợp 4.8") + count per category
  const categoryOptions = useMemo(() => {
    const counts = {};
    playerRows.forEach(r => {
      const c = r.category || "(chưa phân loại)";
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]))
      .map(([cat, n]) => ({ value: cat, label: `${cat} (${n})` }));
  }, [playerRows]);

  const handleSaveConfig = async () => {
    if (!configEditing) return;
    setConfigSaving(true);
    try {
      const payload = {
        zns_template_id: configEditing.zns_template_id || "",
        default_bank_account: configEditing.default_bank_account || "",
        default_bank_owner: configEditing.default_bank_owner || "",
        default_bank_name: configEditing.default_bank_name || "",
        webhook_url: configEditing.webhook_url || "",
        webhook_enabled: !!configEditing.webhook_enabled,
        send_to_all_players: configEditing.send_to_all_players !== false,
      };
      // Chỉ gửi webhook_secret nếu user nhập mới (tránh ghi đè bằng masked preview)
      if (configEditing.webhook_secret_new) {
        payload.webhook_secret = configEditing.webhook_secret_new;
      }
      await api.put(`/pickleague/tournaments/${encodeURIComponent(configEditing.tournament_code)}`, payload);
      toast.success("Đã lưu cấu hình giải");
      setConfigEditing(null);
      await loadTournaments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi lưu cấu hình");
    } finally {
      setConfigSaving(false);
    }
  };

  // v1.3.1: gửi ZNS cho RIÊNG 1 VĐV (per-player), thay vì cả cặp.
  // v1.4.5: thêm param force để admin bypass guard "đã TT" / "pair removed".
  const handleSendPlayer = async (pair_id, player_id, opts = {}) => {
    const { force = false, playerName = "" } = opts;
    if (force) {
      if (!window.confirm(
        `⚠️ FORCE GỬI LẠI\n\nVĐV ${playerName || player_id} đã thanh toán (hoặc đã được gửi/đã rút).\n\nXác nhận gửi lại ZNS?\n(Dùng khi link CK sai, hoặc cần resend nhắc nhở.)`
      )) return;
    }
    const rowKey = `${pair_id}__${player_id}`;
    setSendingRow(rowKey);
    try {
      const url = `/pickleague/tournaments/${encodeURIComponent(selectedCode)}/pairs/${encodeURIComponent(pair_id)}/players/${encodeURIComponent(player_id)}/send-zns${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      if (data.sent > 0) toast.success(`Đã gửi ZNS cho VĐV ${playerName || player_id}${force ? " (FORCE)" : ""}`);
      else if (data.failed > 0) toast.error(`Gửi ZNS thất bại: ${data.failed_details?.[0]?.error || "unknown"}`);
      else toast.warning(`Bỏ qua: ${data.skipped_details?.[0]?.reason || "không có phone hợp lệ"}`);
      await loadPairs(selectedCode);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi gửi ZNS");
    } finally {
      setSendingRow(null);
    }
  };

  // v1.3.1: preview ZNS render data trước khi gửi
  const handlePreviewPlayer = async (pair_id, player_id) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewData(null);
    try {
      const { data } = await api.post(
        `/pickleague/tournaments/${encodeURIComponent(selectedCode)}/preview-zns`,
        { pair_id, player_id },
      );
      setPreviewData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // v1.4.8: Admin HARD delete pair (xóa hẳn data lạ do Pickleague push nhầm).
  const handleAdminDeletePair = async (pair_id, playerNames = "") => {
    const confirmMsg =
      `⚠️ XÓA CẶP KHỎI KARINOX\n\n` +
      `Pair ID: ${pair_id}\n` +
      `VĐV: ${playerNames || "—"}\n\n` +
      `Hành động này sẽ xóa HẲN cặp khỏi DB KARINOX (không phục hồi qua UI).\n` +
      `Audit log vẫn lưu snapshot để truy ngược nếu cần.\n\n` +
      `Chỉ dùng khi VĐV KHÔNG có bên Pickleague (data lạ).\n\n` +
      `Xác nhận xóa?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const url = `/pickleague/admin/tournaments/${encodeURIComponent(selectedCode)}/pairs/${encodeURIComponent(pair_id)}`;
      const { data } = await api.delete(url);
      if (data.deleted > 0) {
        toast.success(`Đã xóa cặp ${pair_id} khỏi KARINOX`);
        await loadPairs(selectedCode);
      } else {
        toast.warning(`Không tìm thấy cặp ${pair_id} để xóa`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi xóa cặp");
    }
  };

  // v1.5.4: Xóa hẳn giải khỏi Karinox (cascade xóa pairs).
  const handleAdminDeleteTournament = async (t) => {
    const pairsCount = t.pairs_count ?? "?";
    const msg =
      `⚠️ XÓA HẲN GIẢI KHỎI KARINOX\n\n` +
      `Tên giải: ${t.tournament_name}\n` +
      `Mã: ${t.tournament_code}\n` +
      `Số VĐV: ${pairsCount}\n\n` +
      `Hành động này sẽ xóa CẢ giải + TẤT CẢ pairs khỏi DB Karinox (KHÔNG sync về Pickleague).\n` +
      `Audit log vẫn lưu snapshot. Dùng khi giải đã kết thúc / data lỗi.\n\n` +
      `XÁC NHẬN xóa?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/pickleague/admin/tournaments/${encodeURIComponent(t.tournament_code)}`);
      toast.success(`Đã xóa giải "${t.tournament_name}" (${data.pairs_deleted || 0} pairs).`);
      if (selectedCode === t.tournament_code) setSelectedCode("");
      await loadTournaments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi xóa giải");
    }
  };

  // v1.5.3: Admin Karinox MANUAL toggle payment_status (cho data lỡ chưa sync từ Pickleague)
  const handleAdminTogglePaid = async (pair_id, currentStatus, playerNames = "") => {
    const willBePaid = currentStatus !== "paid";
    const action = willBePaid ? "ĐÁNH DẤU ĐÃ THANH TOÁN" : "HOÀN TÁC THANH TOÁN";
    const confirmMsg =
      `⚠️ ${action}\n\n` +
      `Pair ID: ${pair_id}\n` +
      `VĐV: ${playerNames || "—"}\n` +
      `Trạng thái hiện tại: ${currentStatus === "paid" ? "Đã TT" : "Chưa TT"}\n\n` +
      `Hành động này CHỈ áp dụng trên Karinox-side (KHÔNG đồng bộ ngược về Pickleague).\n` +
      `Dùng khi: VĐV đã TT bên Pickleague TRƯỚC khi Pickleague v1.4.5 ship → Karinox không nhận sync.\n\n` +
      `Xác nhận?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const action_path = willBePaid ? "mark-paid" : "mark-unpaid";
      const url = `/pickleague/admin/tournaments/${encodeURIComponent(selectedCode)}/pairs/${encodeURIComponent(pair_id)}/${action_path}`;
      const { data } = await api.post(url);
      if (data.modified > 0) {
        toast.success(willBePaid ? `Đã đánh dấu TT cho ${pair_id}` : `Đã hoàn tác TT cho ${pair_id}`);
        await loadPairs(selectedCode);
      } else {
        toast.warning(`Không có thay đổi cho ${pair_id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi đánh dấu TT");
    }
  };

  // v1.3.2: Bulk select helpers
  const toggleRowSelect = (row_key) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(row_key)) next.delete(row_key); else next.add(row_key);
      return next;
    });
  };
  const clearSelection = () => setSelectedRows(new Set());

  // v1.4.2: Bulk restore tất cả pair đã rút bị sai (sau khi fix backend logic replace_all opt-in)
  const handleRestoreAllRemoved = async () => {
    if (!window.confirm("Khôi phục TẤT CẢ VĐV đã rút (đặt lại trạng thái active)?\n\nDùng khi pickleague sync xóa nhầm.")) return;
    try {
      const { data } = await api.post(`/pickleague/tournaments/${encodeURIComponent(selectedCode)}/restore-all-removed`);
      toast.success(`Đã khôi phục ${data.restored} VĐV về trạng thái active`);
      await loadPairs(selectedCode);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi khôi phục");
    }
  };

  // v1.3.2: Gửi ZNS hàng loạt cho các VĐV đã chọn (skip phone invalid + đã TT + đã gửi)
  const handleBulkSendSelected = async () => {
    if (selectedRows.size === 0) { toast.error("Chưa chọn VĐV nào"); return; }
    if (!window.confirm(`Gửi ZNS cho ${selectedRows.size} VĐV đã chọn?`)) return;
    setBulkSending(true);
    // v1.5.4: track progress realtime cho từng VĐV
    const rowKeys = Array.from(selectedRows);
    const playerInfo = new Map(playerRows.map(r => [r.row_key, r]));
    setBulkProgress({
      total: rowKeys.length, done: 0, ok: 0, failed: 0, skipped: 0, items: [],
    });
    try {
      for (let i = 0; i < rowKeys.length; i++) {
        const row_key = rowKeys[i];
        const [pair_id, player_id] = row_key.split("__");
        const info = playerInfo.get(row_key) || {};
        const item = { row_key, name: info.full_name || pair_id, phone: info.phone || "", status: "pending", error: "" };
        try {
          const { data } = await api.post(
            `/pickleague/tournaments/${encodeURIComponent(selectedCode)}/pairs/${encodeURIComponent(pair_id)}/players/${encodeURIComponent(player_id)}/send-zns`,
          );
          if (data.sent > 0) {
            item.status = "sent";
          } else if (data.failed > 0) {
            item.status = "failed";
            item.error = (data.failed_details && (data.failed_details[0]?.error || data.failed_details[0]?.reason)) || "Lỗi không xác định";
          } else {
            item.status = "skipped";
            item.error = (data.skipped_reasons && data.skipped_reasons[0]) || "Bỏ qua";
          }
        } catch (e) {
          item.status = "failed";
          item.error = formatApiError(e.response?.data?.detail) || e.message || "Network error";
        }
        setBulkProgress(prev => ({
          ...prev,
          done: i + 1,
          ok: prev.ok + (item.status === "sent" ? 1 : 0),
          failed: prev.failed + (item.status === "failed" ? 1 : 0),
          skipped: prev.skipped + (item.status === "skipped" ? 1 : 0),
          items: [...prev.items, item],
        }));
      }
      clearSelection();
      await loadPairs(selectedCode);
    } finally {
      setBulkSending(false);
    }
  };

  const handleSendBulk = async () => {
    if (!window.confirm("Gửi ZNS cho TẤT CẢ các cặp chưa TT + chưa gửi ZNS của giải này?")) return;
    setSendingBulk(true);
    try {
      const { data } = await api.post(`/pickleague/tournaments/${encodeURIComponent(selectedCode)}/send-zns-bulk`);
      toast.success(`Đã gửi: ${data.sent}, Lỗi: ${data.failed}, Bỏ qua: ${data.skipped}`);
      await Promise.all([loadTournaments(), loadPairs(selectedCode)]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lỗi gửi ZNS hàng loạt");
    } finally {
      setSendingBulk(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pickleague-tab">
      {/* Header + Refresh */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-100 flex items-center gap-2 flex-wrap">
            <Trophy className="w-4 h-4 text-amber-400" />
            Giải Pickleague (sync từ pickleague.vn qua API)
            {apiVersion && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 font-mono" data-testid="pickleague-api-version-badge">
                <Tag className="w-2.5 h-2.5 inline mr-0.5" />
                API {apiVersion}
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Pickleague tự đẩy danh sách cặp chưa TT sang đây. Anh cấu hình ZNS template ID riêng cho từng giải rồi gửi thủ công.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => window.open("/api/pickleague/integration-guide", "_blank")}
            variant="outline"
            size="sm"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 gap-1.5 text-xs h-8"
            data-testid="pickleague-view-docs-btn"
          >
            <FileText className="w-3 h-3" />
            Tài liệu API
          </Button>
          <Button
            onClick={() => window.open("/api/pickleague/changelog", "_blank")}
            variant="outline"
            size="sm"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 gap-1.5 text-xs h-8"
            data-testid="pickleague-view-changelog-btn"
          >
            <Tag className="w-3 h-3" />
            Changelog
          </Button>
          <Button
            onClick={() => {
              const a = document.createElement("a");
              a.href = "/api/pickleague/integration-guide.md";
              a.download = "PICKLEAGUE_API_INTEGRATION.md";
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }}
            variant="outline"
            size="sm"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-1.5 text-xs h-8"
            data-testid="pickleague-download-docs-btn"
          >
            <Download className="w-3 h-3" />
            Tải .md
          </Button>
          <Button
            onClick={loadTournaments}
            variant="outline"
            size="sm"
            disabled={loading}
            className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 gap-1.5 text-xs h-8"
            data-testid="pickleague-refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Tải lại
          </Button>
        </div>
      </div>

      {/* Tournament list */}
      {tournaments.length === 0 ? (
        <div className="bg-[#0f172a]/60 border border-[#334155] rounded-lg p-6 text-center" data-testid="pickleague-empty">
          <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Chưa có giải Pickleague nào được sync.</p>
          <p className="text-[11px] text-slate-600 italic mt-1">
            Khi pickleague.vn gọi <code>POST /api/pickleague/tournaments/sync</code>, giải sẽ tự xuất hiện ở đây.
          </p>
        </div>
      ) : (
        <div className="grid gap-2" data-testid="pickleague-tournament-list">
          {tournaments.map(t => {
            const isSelected = t.tournament_code === selectedCode;
            const stats = t.stats || {};
            const needConfig = !t.zns_template_id;
            return (
              <div
                key={t.tournament_code}
                className={`bg-[#0f172a]/60 border rounded-lg p-3 transition cursor-pointer ${
                  isSelected ? "border-violet-500/60 bg-violet-500/5" : "border-[#334155] hover:border-slate-500"
                }`}
                onClick={() => setSelectedCode(isSelected ? "" : t.tournament_code)}
                data-testid={`pickleague-tournament-card-${t.tournament_code}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-slate-100 truncate">{t.tournament_name}</h4>
                      <code className="text-[10px] bg-slate-700/50 px-1.5 py-0.5 rounded text-slate-400">{t.tournament_code}</code>
                      {needConfig && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          ⚠️ Chưa cấu hình ZNS
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] flex-wrap">
                      <span className="text-slate-400 flex items-center gap-1">
                        <Users className="w-3 h-3" /> Tổng: <strong className="text-slate-200">{stats.total || 0}</strong>
                      </span>
                      <span className="text-emerald-400">
                        <CheckCircle2 className="w-3 h-3 inline" /> Đã TT: {stats.paid || 0}
                      </span>
                      <span className="text-amber-400">
                        <Clock className="w-3 h-3 inline" /> Chưa TT: {stats.unpaid || 0}
                      </span>
                      <span className="text-cyan-400">
                        <Send className="w-3 h-3 inline" /> ZNS đã gửi: {stats.zns_sent || 0}
                      </span>
                      {(stats.zns_failed || 0) > 0 && (
                        <span className="text-red-400">
                          <XCircle className="w-3 h-3 inline" /> ZNS lỗi: {stats.zns_failed}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfigEditing({
                          tournament_code: t.tournament_code,
                          zns_template_id: t.zns_template_id || "",
                          default_bank_account: t.default_bank_account || "",
                          default_bank_owner: t.default_bank_owner || "",
                          default_bank_name: t.default_bank_name || "",
                          webhook_url: t.webhook_url || "",
                          webhook_secret_preview: t.webhook_secret_preview || "",
                          webhook_secret_new: "",
                          webhook_enabled: !!t.webhook_enabled,
                          send_to_all_players: t.send_to_all_players !== false,
                        });
                      }}
                      className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10 gap-1 text-[11px] h-7"
                      data-testid={`pickleague-config-btn-${t.tournament_code}`}
                    >
                      <SettingsIcon className="w-3 h-3" /> Cấu hình
                    </Button>
                    {/* v1.5.4: Xóa giải */}
                    <Button
                      size="sm" variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAdminDeleteTournament(t);
                      }}
                      title="Xóa giải khỏi Karinox (cascade xóa pairs, KHÔNG sync về Pickleague)"
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10 gap-1 text-[11px] h-7 px-2"
                      data-testid={`pickleague-delete-tournament-btn-${t.tournament_code}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Config modal */}
      {configEditing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setConfigEditing(null)}>
          <div
            className="bg-[#1e293b] border border-[#334155] rounded-lg p-5 max-w-lg w-full space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="pickleague-config-modal"
          >
            <h3 className="text-base font-semibold text-slate-100">
              Cấu hình giải <code className="text-violet-300">{configEditing.tournament_code}</code>
            </h3>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-slate-400">ZNS Template ID <span className="text-red-400">*</span></Label>
                <Input
                  value={configEditing.zns_template_id}
                  onChange={(e) => setConfigEditing(c => ({ ...c, zns_template_id: e.target.value }))}
                  placeholder="Vd: 572684 (4-12 chữ số từ Zalo Console)"
                  className="bg-[#0f172a] border-[#334155] text-slate-100 font-mono text-xs"
                  data-testid="config-template-id-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Template phải có biến: customer_name, tournament_name, category, fee, bank_account, bank_owner, bank_name, transfer_content, deadline.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-400">STK default (optional)</Label>
                  <Input
                    value={configEditing.default_bank_account}
                    onChange={(e) => setConfigEditing(c => ({ ...c, default_bank_account: e.target.value }))}
                    placeholder="9999999999"
                    className="bg-[#0f172a] border-[#334155] text-slate-100 text-xs"
                    data-testid="config-bank-account-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Tên TK default (optional)</Label>
                  <Input
                    value={configEditing.default_bank_owner}
                    onChange={(e) => setConfigEditing(c => ({ ...c, default_bank_owner: e.target.value }))}
                    placeholder="PICKLEAGUE JSC"
                    className="bg-[#0f172a] border-[#334155] text-slate-100 text-xs"
                    data-testid="config-bank-owner-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Ngân hàng default (optional)</Label>
                <Input
                  value={configEditing.default_bank_name}
                  onChange={(e) => setConfigEditing(c => ({ ...c, default_bank_name: e.target.value }))}
                  placeholder="Vietcombank"
                  className="bg-[#0f172a] border-[#334155] text-slate-100 text-xs"
                  data-testid="config-bank-name-input"
                />
              </div>
              <p className="text-[10px] text-slate-500 italic">
                💡 Default chỉ dùng khi pickleague KHÔNG gửi STK/tên TK trong từng cặp. Nếu pickleague gửi → ưu tiên giá trị per-pair.
              </p>

              {/* v1.1: send_to_all_players toggle */}
              <div className="border-t border-slate-700 pt-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <p className="text-xs text-slate-200 font-medium flex items-center gap-1">
                      <Users className="w-3.5 h-3.5 text-violet-400" />
                      Gửi ZNS cho cả 2 VĐV trong cặp
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Bật: gửi cho TẤT CẢ VĐV (mỗi VĐV nhận với customer_name riêng).
                      Tắt: chỉ gửi cho VĐV đầu tiên (đại diện cặp).
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={configEditing.send_to_all_players !== false}
                    onChange={(e) => setConfigEditing(c => ({ ...c, send_to_all_players: e.target.checked }))}
                    className="w-4 h-4 accent-violet-500"
                    data-testid="config-send-to-all-players-toggle"
                  />
                </label>
              </div>

              {/* v1.1: Webhook config */}
              <div className="border-t border-slate-700 pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-200 font-medium flex items-center gap-1">
                    <Webhook className="w-3.5 h-3.5 text-cyan-400" />
                    Webhook ngược (real-time, thay cho poll status)
                  </p>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <span className="text-[10px] text-slate-400">{configEditing.webhook_enabled ? "Bật" : "Tắt"}</span>
                    <input
                      type="checkbox"
                      checked={!!configEditing.webhook_enabled}
                      onChange={(e) => setConfigEditing(c => ({ ...c, webhook_enabled: e.target.checked }))}
                      className="w-4 h-4 accent-cyan-500"
                      data-testid="config-webhook-enabled-toggle"
                    />
                  </label>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Webhook URL (pickleague cung cấp)</Label>
                  <Input
                    value={configEditing.webhook_url || ""}
                    onChange={(e) => setConfigEditing(c => ({ ...c, webhook_url: e.target.value }))}
                    placeholder="https://pickleague.vn/api/karinox/webhook"
                    className="bg-[#0f172a] border-[#334155] text-slate-100 font-mono text-xs"
                    data-testid="config-webhook-url-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400 flex items-center gap-1.5">
                    Webhook Secret (HMAC-SHA256 sign)
                    {configEditing.webhook_secret_preview && (
                      <span className="text-[10px] text-emerald-400 font-mono">
                        Hiện tại: {configEditing.webhook_secret_preview}
                      </span>
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={configEditing.webhook_secret_new || ""}
                    onChange={(e) => setConfigEditing(c => ({ ...c, webhook_secret_new: e.target.value }))}
                    placeholder={configEditing.webhook_secret_preview ? "Để trống nếu giữ secret cũ, paste mới để đổi" : "Paste secret để pickleague verify signature..."}
                    className="bg-[#0f172a] border-[#334155] text-slate-100 font-mono text-xs"
                    data-testid="config-webhook-secret-input"
                  />
                </div>
                <p className="text-[10px] text-slate-500 italic">
                  💡 Mỗi khi KARINOX gửi ZNS xong cho 1 VĐV → POST <code className="text-slate-400">webhook_url</code> với body {"{event, tournament_code, pair_id, player_id, success, msg_id, error}"} kèm header <code className="text-slate-400">X-Karinox-Signature: sha256=...</code>. Pickleague verify HMAC để chống giả mạo.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setConfigEditing(null)} className="border-slate-500/40 text-slate-300 text-xs h-8" data-testid="config-cancel-btn">
                Hủy
              </Button>
              <Button onClick={handleSaveConfig} disabled={configSaving} className="bg-violet-500 hover:bg-violet-600 text-white text-xs h-8 gap-1.5" data-testid="config-save-btn">
                {configSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Lưu cấu hình
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Selected tournament: pairs table */}
      {selected && (
        <div className="bg-[#0f172a]/60 border border-violet-500/30 rounded-lg p-3 space-y-3" data-testid="pickleague-pairs-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h4 className="text-sm font-semibold text-slate-100">
                Danh sách cặp: <span className="text-violet-300">{selected.tournament_name}</span>
              </h4>
              <p className="text-[11px] text-slate-500">
                Pairs: {pairs.total || 0} · Cập nhật gần nhất: {selected.last_sync_at ? new Date(selected.last_sync_at).toLocaleString("vi-VN") : "-"}
              </p>
            </div>
            <Button
              onClick={handleSendBulk}
              disabled={sendingBulk || !selected.zns_template_id}
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 text-xs h-8"
              data-testid="pickleague-send-bulk-btn"
            >
              {sendingBulk ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Gửi ZNS hàng loạt (chưa TT)
            </Button>
          </div>

          {/* v1.5.5: Stat cards CRM-style — click to filter */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="pickleague-pairs-filters">
            {[
              { key: "all",          label: "Tổng",           count: rowStats.total,        color: "violet",   metric: "all" },
              { key: "paid",         label: "Đã TT",          count: rowStats.paid,         color: "emerald",  metric: "paid" },
              { key: "unpaid",       label: "Chưa TT",        count: rowStats.unpaid,       color: "amber",    metric: "unpaid" },
              { key: "zns_not_sent", label: "ZNS chưa gửi",   count: rowStats.zns_not_sent, color: "sky",      metric: "zns_not_sent" },
              { key: "zns_failed",   label: "ZNS lỗi",        count: rowStats.zns_failed,   color: "red",      metric: "zns_failed" },
              { key: "removed",      label: "Đã rút",         count: rowStats.removed,      color: "slate",    metric: "removed" },
            ].map(opt => {
              const active = filter === opt.key;
              const colorMap = {
                violet:  active ? "bg-violet-500/20 border-violet-500 text-violet-300"   : "border-violet-500/30 hover:bg-violet-500/5 text-slate-300",
                emerald: active ? "bg-emerald-500/20 border-emerald-500 text-emerald-300" : "border-emerald-500/30 hover:bg-emerald-500/5 text-slate-300",
                amber:   active ? "bg-amber-500/20 border-amber-500 text-amber-300"      : "border-amber-500/30 hover:bg-amber-500/5 text-slate-300",
                sky:     active ? "bg-sky-500/20 border-sky-500 text-sky-300"            : "border-sky-500/30 hover:bg-sky-500/5 text-slate-300",
                red:     active ? "bg-red-500/20 border-red-500 text-red-300"            : "border-red-500/30 hover:bg-red-500/5 text-slate-300",
                slate:   active ? "bg-slate-500/20 border-slate-400 text-slate-200"      : "border-slate-500/30 hover:bg-slate-500/5 text-slate-300",
              };
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFilter(opt.key)}
                  className={`relative flex flex-col items-start p-2.5 rounded-lg border transition ${colorMap[opt.color]}`}
                  data-testid={`pickleague-filter-${opt.key}`}
                >
                  <span className="text-[10px] uppercase tracking-wider font-medium opacity-90">{opt.label}</span>
                  <span className="text-xl font-bold font-mono mt-0.5">{opt.count}</span>
                </button>
              );
            })}
          </div>

          {/* v1.5.5: Filter theo nội dung thi đấu (category) */}
          {categoryOptions.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="pickleague-category-filter">
              <span className="text-[11px] text-slate-400 flex items-center gap-1"><Tag className="w-3 h-3" />Nội dung thi đấu:</span>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-[#1e293b] border border-[#334155] text-slate-200 text-xs rounded px-2 py-1 max-w-xs"
                data-testid="pickleague-category-select"
              >
                <option value="all">Tất cả nội dung ({playerRows.length})</option>
                {categoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {categoryFilter !== "all" && (
                <button
                  onClick={() => setCategoryFilter("all")}
                  className="text-[10px] text-amber-400 hover:underline"
                >
                  ✕ Bỏ lọc
                </button>
              )}
            </div>
          )}

          {/* v1.4.2: Banner cảnh báo khi filter "Đã rút" — kèm nút "Khôi phục tất cả" */}
          {filter === "removed" && (
            <div className="flex items-center justify-between flex-wrap gap-2 bg-amber-500/5 border border-amber-500/30 rounded px-3 py-2" data-testid="pickleague-removed-banner">
              <span className="text-xs text-amber-300 flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Đang xem danh sách <strong>đã rút</strong>. Nếu có VĐV vẫn đăng ký bên pickleague nhưng bị đưa vào đây nhầm (do bug sync cũ),
                  bấm "Khôi phục tất cả" để đặt lại active.
                </span>
              </span>
              <Button
                size="sm"
                onClick={handleRestoreAllRemoved}
                className="bg-amber-500 text-[#0f172a] font-semibold hover:bg-amber-400 gap-1 text-[11px] h-7 flex-shrink-0"
                data-testid="pickleague-restore-all-btn"
              >
                <RefreshCw className="w-3 h-3" /> Khôi phục tất cả
              </Button>
            </div>
          )}

          {/* v1.3.1: Bảng VĐV (1 row / VĐV theo pattern CRM giải đấu) */}
          {pairsLoading ? (
            <p className="text-xs text-slate-500 text-center py-4">Đang tải…</p>
          ) : playerRows.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-4">Không có VĐV nào khớp filter.</p>
          ) : (
            <div className="space-y-2">
              {/* v1.3.2: Bulk action bar — chỉ hiện khi có row được chọn */}
              {selectedRows.size > 0 && (
                <div className="flex items-center justify-between flex-wrap gap-2 bg-sky-500/5 border border-sky-500/30 rounded px-3 py-2" data-testid="pickleague-bulk-action-bar">
                  <span className="text-xs text-sky-300 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Đã chọn <strong className="text-sky-200">{selectedRows.size}</strong> / {eligibleRowKeys.length} VĐV
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearSelection}
                      disabled={bulkSending}
                      className="border-slate-500/40 text-slate-400 hover:bg-slate-500/10 text-[11px] h-7"
                      data-testid="pickleague-clear-selection-btn"
                    >
                      Bỏ chọn
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleBulkSendSelected}
                      disabled={bulkSending || !selected?.zns_template_id}
                      className="bg-amber-500 text-[#0f172a] font-semibold hover:bg-amber-400 gap-1 text-[11px] h-7"
                      data-testid="pickleague-bulk-send-btn"
                    >
                      {bulkSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Gửi ZNS cho {selectedRows.size} VĐV
                    </Button>
                  </div>
                </div>
              )}
            <div className="overflow-x-auto bg-[#0f172a]/80 border border-[#334155] rounded">
              <table className="w-full text-[11px]" data-testid="pickleague-players-table">
                <thead className="bg-[#1e293b] text-slate-400 uppercase text-[10px]">
                  <tr>
                    <th className="px-2 py-2 text-left w-8">
                      <Checkbox
                        checked={
                          eligibleRowKeys.length > 0 &&
                          eligibleRowKeys.every(k => selectedRows.has(k))
                        }
                        onCheckedChange={(checked) => {
                          if (checked) setSelectedRows(new Set(eligibleRowKeys));
                          else clearSelection();
                        }}
                        className="border-slate-500"
                        data-testid="pickleague-select-all-cb"
                      />
                    </th>
                    <th className="px-2 py-2 text-left w-12">STT</th>
                    <th className="px-2 py-2 text-left">Tên VĐV</th>
                    <th className="px-2 py-2 text-left">SĐT</th>
                    <th className="px-2 py-2 text-left">Trình</th>
                    <th className="px-2 py-2 text-left">Đồng đội</th>
                    <th className="px-2 py-2 text-left">ND chuyển khoản</th>
                    <th className="px-2 py-2 text-left">Mã HĐ</th>
                    <th className="px-2 py-2 text-right">Phí</th>
                    <th className="px-2 py-2 text-center">Thanh toán</th>
                    <th className="px-2 py-2 text-center w-20">Gửi ZNS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {displayRows.map((r) => {
                    const isExpanded = expandedPair === r.row_key;
                    const isSending = sendingRow === r.row_key;
                    const isSelected = selectedRows.has(r.row_key);
                    const isEligible = r.phone_valid && r.payment_status !== "paid" && !r.is_removed;
                    return (
                      <React.Fragment key={r.row_key}>
                        <tr
                          className={`hover:bg-slate-700/30 ${r.payment_status === "paid" ? "bg-emerald-500/5" : ""} ${isSelected ? "bg-sky-500/10" : ""} ${r.is_removed ? "opacity-50 grayscale" : ""}`}
                          data-testid={`pickleague-player-row-${r.row_key}`}
                        >
                          <td className="px-2 py-1.5">
                            <Checkbox
                              checked={isSelected}
                              disabled={!isEligible}
                              onCheckedChange={() => isEligible && toggleRowSelect(r.row_key)}
                              className="border-slate-500"
                              data-testid={`pickleague-cb-${r.row_key}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-slate-400 font-mono text-[10px]">{r.display_stt}</td>
                          <td className="px-2 py-1.5 text-slate-200">
                            <button
                              onClick={() => setExpandedPair(isExpanded ? null : r.row_key)}
                              className="text-left hover:text-violet-300"
                            >
                              <div className="font-medium flex items-center gap-1.5">
                                {r.full_name || "—"}
                                {r.is_removed && (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30 uppercase tracking-wider" title={`Đã rút (${r.removed_reason || "removed"})`}>
                                    Đã rút
                                  </span>
                                )}
                              </div>
                              {r.province && <div className="text-[10px] text-slate-600">{r.province}</div>}
                            </button>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={`font-mono text-[10px] ${r.phone_valid ? "text-slate-400" : "text-red-400"}`} title={!r.phone_valid && r.phone ? "Phone format không hợp lệ" : ""}>
                              {r.phone || "—"}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 text-[10px]">{r.category || "—"}</span>
                          </td>
                          <td className="px-2 py-1.5 text-slate-400 text-[11px]">
                            {r.partner_name ? (
                              <div>
                                <div>{r.partner_name}</div>
                                {r.partner_phone && <div className="text-[9px] text-slate-600 font-mono">{r.partner_phone}</div>}
                              </div>
                            ) : r.missing_partner ? (
                              <span className="inline-flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/30" title="Đây là nội dung đôi nhưng pickleague chưa push đủ thông tin đồng đội. Có thể đội trưởng đăng ký trước, đồng đội add sau.">
                                <AlertCircle className="w-3 h-3" /> Thiếu đồng đội
                              </span>
                            ) : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-2 py-1.5">
                            {r.transfer_content ? (
                              <code className="text-[10px] text-sky-400 bg-[#0f172a] px-1.5 py-0.5 rounded border border-[#334155]/50 font-mono">
                                {r.transfer_content}
                              </code>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/30" title="Pickleague chưa push transfer_content. Khi gửi ZNS, KARINOX sẽ fallback dùng pair_id làm Mã hợp đồng.">
                                <AlertCircle className="w-3 h-3" /> Thiếu ND CK
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 text-[10px] font-mono">#{r.contract_number}</td>
                          <td className="px-2 py-1.5 text-right text-slate-300 font-mono">
                            {/* v1.5.2: tooltip breakdown nếu Pickleague gửi (combined payment) */}
                            {r.breakdown && (r.breakdown.sponsor_amount_vnd > 0) ? (
                              <span
                                title={`Lệ phí: ${(r.breakdown.entry_fee_vnd || 0).toLocaleString("vi-VN")}đ\nTài trợ: ${(r.breakdown.sponsor_amount_vnd || 0).toLocaleString("vi-VN")}đ`}
                                className="cursor-help underline decoration-dotted decoration-amber-500/50"
                              >
                                {(r.fee || 0).toLocaleString("vi-VN")}đ
                              </span>
                            ) : (
                              <>{(r.fee || 0).toLocaleString("vi-VN")}đ</>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {r.payment_status === "paid" ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-lime-500/15 text-lime-400 border border-lime-500/30 text-[10px]">
                                <CheckCircle2 className="w-3 h-3" /> Đã TT
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20 text-[10px]">
                                <XCircle className="w-3 h-3" /> Chưa TT
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => handlePreviewPlayer(r.pair_id, r.player_id)}
                                title="Xem trước tin ZNS"
                                className="p-1 rounded text-slate-500 hover:text-sky-400 transition-colors"
                                data-testid={`pickleague-preview-btn-${r.row_key}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleSendPlayer(r.pair_id, r.player_id, { playerName: r.full_name })}
                                disabled={isSending || r.payment_status === "paid" || !selected.zns_template_id || !r.phone_valid || r.is_removed}
                                title={r.is_removed ? "VĐV đã rút khỏi giải" : !r.phone_valid ? "Phone không hợp lệ" : r.payment_status === "paid" ? "Đã TT, không cần gửi" : "Gửi ZNS"}
                                className={`p-1 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                                  r.zns_status === "sent" ? "text-lime-400 hover:text-lime-300" :
                                  r.zns_status === "failed" ? "text-red-400 hover:text-red-300" :
                                  "text-slate-500 hover:text-amber-400"
                                }`}
                                data-testid={`pickleague-send-btn-${r.row_key}`}
                              >
                                {isSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                                 r.zns_status === "sent" ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                                 r.zns_status === "failed" ? <XCircle className="w-3.5 h-3.5" title={r.zns_error} /> :
                                 <SendHorizonal className="w-3.5 h-3.5" />}
                              </button>
                              {/* v1.4.5: Force gửi lại — chỉ hiện khi row đã TT / đã sent / removed (bypass guard) */}
                              {(r.payment_status === "paid" || r.zns_status === "sent" || r.is_removed) && r.phone_valid && selected.zns_template_id && (
                                <button
                                  onClick={() => handleSendPlayer(r.pair_id, r.player_id, { force: true, playerName: r.full_name })}
                                  disabled={isSending}
                                  title="Force gửi lại (bypass guard đã TT / đã sent / đã rút)"
                                  className="p-1 rounded text-slate-500 hover:text-orange-400 transition-colors disabled:opacity-30"
                                  data-testid={`pickleague-force-btn-${r.row_key}`}
                                >
                                  <RotateCw className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {/* v1.5.3: Admin manual toggle payment_status (cho data lỡ chưa sync) */}
                              <button
                                onClick={() => handleAdminTogglePaid(r.pair_id, r.payment_status, r.full_name + (r.partner_name ? ` & ${r.partner_name}` : ""))}
                                title={r.payment_status === "paid"
                                  ? "Hoàn tác TT (admin Karinox manual — KHÔNG sync về Pickleague)"
                                  : "Đánh dấu đã TT (admin Karinox manual — dành cho data lỡ chưa sync từ Pickleague)"}
                                className={`p-1 rounded transition-colors ${
                                  r.payment_status === "paid"
                                    ? "text-emerald-500 hover:text-amber-500"
                                    : "text-slate-500 hover:text-emerald-500"
                                }`}
                                data-testid={`pickleague-toggle-paid-btn-${r.row_key}`}
                              >
                                {r.payment_status === "paid"
                                  ? <WalletCards className="w-3.5 h-3.5" />
                                  : <Wallet className="w-3.5 h-3.5" />
                                }
                              </button>
                              {/* v1.4.8: Admin hard delete pair (data lạ không có bên Pickleague) */}
                              <button
                                onClick={() => handleAdminDeletePair(r.pair_id, r.full_name + (r.partner_name ? ` & ${r.partner_name}` : ""))}
                                title="Xóa cặp khỏi KARINOX (data lạ, KHÔNG có bên Pickleague)"
                                className="p-1 rounded text-slate-500 hover:text-red-500 transition-colors"
                                data-testid={`pickleague-delete-btn-${r.row_key}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-slate-900/50">
                            <td colSpan={11} className="px-3 py-2">
                              <pre className="text-[10px] text-slate-300 font-mono overflow-x-auto whitespace-pre-wrap">
{JSON.stringify((pairs.pairs || []).find(p => p.pair_id === r.pair_id) || {}, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-[10px] text-slate-600 px-3 py-2 border-t border-[#334155]">
                Hiển thị {displayRows.length} / {playerRows.length} VĐV ({(pairs.pairs || []).length} cặp){filter !== "all" ? ` · filter: ${filter}` : ""}{categoryFilter !== "all" ? ` · ${categoryFilter}` : ""}
              </div>
            </div>
            </div>
          )}

          {/* v1.1: Webhook logs panel */}
          {selected.webhook_url && (
            <div className="bg-cyan-500/5 border border-cyan-500/20 rounded p-3 space-y-2 mt-3" data-testid="pickleague-webhook-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-cyan-300 font-medium flex items-center gap-1.5">
                  <Webhook className="w-3.5 h-3.5" />
                  Webhook logs (KARINOX → Pickleague)
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${selected.webhook_enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-400"}`}>
                    {selected.webhook_enabled ? "ĐANG BẬT" : "ĐÃ TẮT"}
                  </span>
                </p>
                <div className="flex gap-1.5">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => loadWebhookLogs(selectedCode)}
                    disabled={webhookLogsLoading}
                    className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 gap-1 text-[10px] h-7"
                    data-testid="pickleague-webhook-logs-refresh-btn"
                  >
                    {webhookLogsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Tải lại
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleTestWebhook(selectedCode)}
                    disabled={testingWebhook || !selected.webhook_enabled}
                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 gap-1 text-[10px] h-7"
                    data-testid="pickleague-webhook-test-btn"
                  >
                    {testingWebhook ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Test ngay
                  </Button>
                </div>
              </div>
              {webhookLogs && (
                <div className="text-[10px] text-slate-400 mb-1">
                  ✓ Thành công: <strong className="text-emerald-400">{webhookLogs.success_count || 0}</strong>
                  {" · "}
                  ✗ Lỗi: <strong className="text-red-400">{webhookLogs.fail_count || 0}</strong>
                </div>
              )}

              {/* v1.2: Protection state */}
              {webhookState && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2" data-testid="webhook-protection-state">
                  {/* Rate limit card */}
                  <div className={`border rounded p-2 ${
                    (webhookState.rate_limit?.current_count || 0) >= (webhookState.rate_limit?.limit || 200) * 0.8
                      ? "bg-amber-500/10 border-amber-500/30" : "bg-[#0f172a]/60 border-[#334155]"
                  }`}>
                    <p className="text-[10px] text-slate-400 uppercase mb-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Rate Limit
                    </p>
                    <p className="text-xs text-slate-200 font-mono">
                      <strong>{webhookState.rate_limit?.current_count || 0}</strong>
                      <span className="text-slate-500"> / {webhookState.rate_limit?.limit || 200} req</span>
                      <span className="text-slate-500 text-[10px]"> trong {webhookState.rate_limit?.window_sec || 60}s</span>
                    </p>
                    {webhookState.rate_limit?.window_remaining_sec > 0 && (
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        Reset sau {webhookState.rate_limit.window_remaining_sec}s
                      </p>
                    )}
                  </div>
                  {/* Circuit breaker card */}
                  <div className={`border rounded p-2 ${
                    webhookState.circuit_breaker?.is_open
                      ? "bg-red-500/10 border-red-500/40" : "bg-[#0f172a]/60 border-[#334155]"
                  }`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Circuit Breaker
                      </p>
                      {webhookState.circuit_breaker?.is_open && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => handleResetCircuit(selectedCode)}
                          disabled={resettingCircuit}
                          className="border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10px] h-5 px-1.5 gap-1"
                          data-testid="webhook-reset-circuit-btn"
                        >
                          {resettingCircuit ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />}
                          Reset
                        </Button>
                      )}
                    </div>
                    <p className="text-xs font-mono mt-1">
                      {webhookState.circuit_breaker?.is_open ? (
                        <span className="text-red-300 font-bold">🔴 ĐANG MỞ ({webhookState.circuit_breaker?.open_remaining_sec}s nữa)</span>
                      ) : (
                        <span className="text-emerald-300">🟢 ĐÓNG (OK)</span>
                      )}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Fail liên tiếp: <strong className="text-slate-300">{webhookState.circuit_breaker?.failure_count || 0}</strong>
                      <span className="text-slate-600"> / {webhookState.circuit_breaker?.fail_threshold || 10}</span>
                    </p>
                    {webhookState.circuit_breaker?.last_error && (
                      <p className="text-[10px] text-red-400 mt-0.5 truncate" title={webhookState.circuit_breaker.last_error}>
                        Lỗi cuối: {webhookState.circuit_breaker.last_error}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {!webhookLogs ? (
                <p className="text-[11px] text-slate-500 text-center py-2">Bấm "Tải lại" để xem logs</p>
              ) : (webhookLogs.entries || []).length === 0 ? (
                <p className="text-[11px] text-slate-500 text-center py-2 italic">Chưa có webhook call nào. Bấm "Test ngay" để verify URL pickleague.</p>
              ) : (
                <div className="bg-[#0f172a]/80 rounded overflow-hidden max-h-80 overflow-y-auto">
                  <table className="w-full text-[10px]">
                    <thead className="bg-[#1e293b] text-slate-400 uppercase sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Thời gian</th>
                        <th className="px-2 py-1.5 text-left">Event</th>
                        <th className="px-2 py-1.5 text-left">Pair / Player</th>
                        <th className="px-2 py-1.5 text-center">Status</th>
                        <th className="px-2 py-1.5 text-center">Attempts</th>
                        <th className="px-2 py-1.5 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {webhookLogs.entries.map((log, i) => (
                        <tr key={i} className={log.success ? "" : "bg-red-500/5"}>
                          <td className="px-2 py-1 font-mono text-slate-300 whitespace-nowrap">
                            {new Date(log.created_at).toLocaleString("vi-VN")}
                          </td>
                          <td className="px-2 py-1"><code className="bg-slate-700/50 px-1 rounded text-cyan-300">{log.event}</code></td>
                          <td className="px-2 py-1 text-slate-300 font-mono">{log.pair_id || "-"}{log.payload?.player_id ? ` / ${log.payload.player_id}` : ""}</td>
                          <td className="px-2 py-1 text-center">
                            {log.success ? (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">{log.status_code} OK</span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-300">{log.status_code || "ERR"}</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-center text-slate-400">{log.attempts}</td>
                          <td className="px-2 py-1 text-red-300 max-w-[200px] truncate" title={log.error}>{log.error || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* v1.3.1: Preview ZNS Dialog (per-player) */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="pickleague-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] font-heading flex items-center gap-2">
              <Eye className="w-4 h-4 text-sky-400" />
              Xem trước tin nhắn ZNS (1 VĐV)
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>
          ) : previewData ? (
            <div className="space-y-3">
              <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-slate-600 font-mono">#1</span>
                    <span className="text-sm text-slate-200 font-medium">{previewData.full_name}</span>
                    <span className={`text-xs font-mono ${previewData.phone_valid ? "text-slate-500" : "text-red-400"}`}>{previewData.phone || "—"}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 border">{previewData.category}</span>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${previewData.payment_status === "paid" ? "bg-lime-500/15 text-lime-400 border-lime-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}`}>
                    {previewData.payment_status === "paid" ? "Đã TT" : "Chưa TT"}
                  </span>
                </div>
                <div className="bg-[#1e293b] rounded-md p-3 border border-[#334155]/50 space-y-1.5">
                  <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Nội dung ZNS gửi tới Zalo</p>
                  <p className="text-xs text-slate-300">
                    Karinox trân trọng thông báo đến Quý khách lệ phí trình{" "}
                    <span className="text-amber-400 font-medium">{previewData.variables?.trinh_vdv || "—"}</span> như sau:
                  </p>
                  <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                    <span className="text-slate-500">Quý khách</span>
                    <span className="text-lime-400 font-medium">{previewData.variables?.customer_name}</span>
                    <span className="text-slate-500">Mã hợp đồng</span>
                    <span className="text-sky-400 font-mono">#{previewData.variables?.contract_number}</span>
                    <span className="text-slate-500">Số tiền</span>
                    <span className="text-lime-400 font-bold">{previewData.variables?.transfer_amount || "—"}</span>
                    <span className="text-slate-500">Nội dung CK</span>
                    <span className="text-cyan-400 font-mono text-[10px] break-all">{previewData.variables?.bank_transfer_note || "—"}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 italic">Ghi chú: Quý khách vui lòng thanh toán để hợp lệ bốc thăm thi đấu.</p>
                </div>
                {previewData.transfer_content && (
                  <p className="text-[10px] text-slate-600">
                    ND chuyển khoản: <code className="text-sky-400">{previewData.transfer_content}</code>
                    {previewData.partner_name && <span className="ml-2">Đồng đội: {previewData.partner_name}</span>}
                  </p>
                )}
                {!previewData.phone_valid && previewData.phone && (
                  <p className="text-[10px] text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Phone không hợp lệ — không thể gửi ZNS cho VĐV này.
                  </p>
                )}
                {!previewData.template_id && (
                  <p className="text-[10px] text-amber-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Giải chưa cấu hình ZNS Template ID.
                  </p>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-[#334155] text-slate-300 hover:bg-[#334155]" data-testid="pickleague-preview-close-btn">Đóng</Button>
            {previewData && previewData.phone_valid && previewData.template_id && previewData.payment_status !== "paid" && (
              <Button
                onClick={() => {
                  setPreviewOpen(false);
                  handleSendPlayer(previewData.pair_id, previewData.player_id, { playerName: previewData.full_name });
                }}
                disabled={sendingRow != null}
                className="bg-amber-500 text-[#0f172a] font-semibold hover:bg-amber-400 gap-1"
                data-testid="pickleague-preview-send-btn"
              >
                <Send className="w-3.5 h-3.5" /> Xác nhận gửi
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v1.5.4: Bulk Send Progress Dialog — realtime log */}
      <Dialog open={!!bulkProgress} onOpenChange={(o) => { if (!o && !bulkSending) setBulkProgress(null); }}>
        <DialogContent className="bg-[#0f172a] border-[#334155] max-w-2xl" data-testid="pickleague-bulk-progress-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-50 flex items-center gap-2">
              <SendHorizonal className="w-5 h-5 text-amber-400" />
              Gửi ZNS hàng loạt {bulkSending ? "(đang chạy...)" : "— hoàn tất"}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              {bulkProgress && `Tiến độ: ${bulkProgress.done}/${bulkProgress.total} VĐV · `}
              <span className="text-emerald-400">✓ {bulkProgress?.ok || 0}</span> ·{" "}
              <span className="text-red-400">✗ {bulkProgress?.failed || 0}</span> ·{" "}
              <span className="text-slate-500">− {bulkProgress?.skipped || 0}</span>
            </DialogDescription>
          </DialogHeader>
          {bulkProgress && (
            <>
              {/* Progress bar */}
              <div className="w-full h-2 bg-[#1e293b] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-emerald-500 transition-all"
                  style={{ width: `${(bulkProgress.done / Math.max(1, bulkProgress.total)) * 100}%` }}
                  data-testid="pickleague-bulk-progress-bar"
                />
              </div>
              {/* Log list — realtime, autoscroll bottom via flex-col-reverse trick */}
              <div className="max-h-[400px] overflow-y-auto border border-[#334155] rounded-lg bg-[#1e293b] divide-y divide-[#334155]/50">
                {bulkProgress.items.length === 0 ? (
                  <div className="p-6 text-center text-slate-500 text-xs">Đang khởi tạo...</div>
                ) : (
                  bulkProgress.items.slice().reverse().map((item, idx) => (
                    <div
                      key={`${item.row_key}-${idx}`}
                      className="flex items-center gap-3 px-3 py-2 text-xs"
                      data-testid={`pickleague-bulk-item-${bulkProgress.items.length - 1 - idx}`}
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        item.status === "sent" ? "bg-emerald-500" :
                        item.status === "failed" ? "bg-red-500" :
                        item.status === "skipped" ? "bg-slate-500" : "bg-amber-500 animate-pulse"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-200 font-medium truncate">{item.name}</div>
                        {item.phone && <div className="text-slate-500 font-mono text-[10px]">{item.phone}</div>}
                        {item.error && <div className="text-red-400 text-[10px] truncate" title={item.error}>{item.error}</div>}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        item.status === "sent" ? "bg-emerald-500/20 text-emerald-300" :
                        item.status === "failed" ? "bg-red-500/20 text-red-300" :
                        item.status === "skipped" ? "bg-slate-500/20 text-slate-300" : "bg-amber-500/20 text-amber-300"
                      }`}>
                        {item.status === "sent" ? "✓ Sent" : item.status === "failed" ? "✗ Fail" : item.status === "skipped" ? "− Skip" : "..."}
                      </span>
                    </div>
                  ))
                )}
              </div>
              {!bulkSending && (
                <DialogFooter>
                  <Button
                    onClick={() => setBulkProgress(null)}
                    className="bg-amber-500 text-[#0f172a] font-semibold hover:bg-amber-400"
                    data-testid="pickleague-bulk-progress-close-btn"
                  >
                    Đóng
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
